import {
  GetAiModelRequest,
  GetSpaceRequest,
  PollGetSpaceAccessControlRequest
} from '@eggplant/api-requests'
import {
  DocumentTitleContext,
  ModelConsumer,
  SpaceConsumer,
  SpaceContext
} from '@eggplant/context'
import {
  DASHBOARDS,
  routeLabels
} from '@eggplant/routing'
import type { GetAiModelResponse } from '@eggplant/types'
import {
  SPACE_ACCESS,
  type Space,
  type SpaceAccess
} from '@eggplant/types'
import {
  navigate,
  useLocation
} from '@reach/router'
import { SkipNavLink } from '@reach/skip-nav'
import '@reach/skip-nav/styles.css'
import type { CSSProperties } from 'react'
import {
  useContext,
  useEffect,
  useRef,
  useState
} from 'react'
import { SKIP_NAVIGATION } from './constants'
import MainDaiRoute from './MainDaiRoute'
import SideNav from './SideNav/SideNav'
import Suspense from './Suspense'

const wrapperStyle: CSSProperties = {
  display: 'flex',
  height: '100%',
  flexDirection: 'row',
  alignItems: 'flex-start'
}

const srOnly: CSSProperties = {
  position: 'absolute',
  padding: '10px',
  overflow: 'hidden'
}

const labels = Object.keys(routeLabels)

const getTabLabel = (routeLabel: string, title?: string) => title
  ? `${routeLabel}: ${title}`
  : `DAI - ${routeLabel}`

const Router = () => {
  const { pathname } = useLocation()
  const spaceContext = useContext(SpaceContext)
  const documentTitleContext = useContext(DocumentTitleContext)
  const [isModelIdChecked, setIsModelIdChecked] = useState(false)
  const prevPathnameRef = useRef(pathname)

  const currentDocumentTitle = documentTitleContext?.name

  useEffect(() => {
    const prevSegments = prevPathnameRef.current.split('/').filter(Boolean)
    const currentSegments = pathname.split('/').filter(Boolean)

    const prevDepth = prevSegments.length
    const currentDepth = currentSegments.length

    if (currentDepth < prevDepth || prevSegments[0] !== currentSegments[0]) {
      documentTitleContext?.setName(undefined)
    }

    // eslint-disable-next-line fp/no-mutation
    prevPathnameRef.current = pathname

    // eslint-disable-next-line fp/no-mutating-methods
    const label = pathname
      .split('/')
      .reverse() // match children before parents
      .find(
        token => labels.includes(token)
      ) as keyof typeof routeLabels | undefined

    // eslint-disable-next-line fp/no-mutation
    document.title = label
      ? getTabLabel(routeLabels[label], currentDocumentTitle)
      : 'Eggplant DAI'
  }, [pathname, currentDocumentTitle, documentTitleContext])

  useEffect(() => {
    if (spaceContext?.selectedSpaceId && pathname.startsWith('/spaces')) {
      void navigate(`/${DASHBOARDS}`)
    }
  }, [spaceContext?.selectedSpaceId, pathname])

  const handleGetSpaceSuccess = (space: Space) => {
    spaceContext?.setSelectedSpaceName(space.name)
  }

  const handlePollSuccess = (space: SpaceAccess) => {
    const accessRole = space.resource_access[0]

    if (accessRole === SPACE_ACCESS.admin) {
      spaceContext?.setRole('dai:admin')
    }

    if (accessRole === SPACE_ACCESS.viewer) {
      spaceContext?.setRole('dai:viewer')
    }

    if (accessRole === SPACE_ACCESS.user) {
      spaceContext?.setRole('dai:user')
    }
  }

  const handleGetSpaceError = () => {
    spaceContext?.setSelectedSpaceId('')
  }

  const renderSpaceCheckRoute = () =>
    spaceContext?.selectedSpaceId &&
      <GetSpaceRequest
        onError={handleGetSpaceError}
        onSuccess={handleGetSpaceSuccess}
        spaceId={spaceContext.selectedSpaceId}
      >
        {
          () =>
            <>
              <MainDaiRoute />
              <SpaceConsumer>
                {
                  spaceContext =>
                    <ModelConsumer>
                      {
                        modelContext => {
                          const handleGetModelSuccess = (data: GetAiModelResponse) => {
                            const modelSpaceId = data.space_id
                            const currentSpaceId = spaceContext?.selectedSpaceId

                            setIsModelIdChecked(true)

                            if(modelSpaceId !== currentSpaceId) {
                              modelContext.setSelectedModelId('')
                            }
                          }

                          const handleGetModelError = () => {
                            modelContext.setSelectedModelId('')

                            setIsModelIdChecked(true)
                          }

                          return (
                            !isModelIdChecked && modelContext.selectedModelId &&
                              <GetAiModelRequest
                                hasSpinner={false}
                                id={modelContext.selectedModelId}
                                onError={handleGetModelError}
                                onSuccess={handleGetModelSuccess}
                              />
                          )
                        }
                      }
                    </ModelConsumer>
                }
              </SpaceConsumer>
            </>
        }
      </GetSpaceRequest>

  return (
    <div style={wrapperStyle}>
      {
        // Prevent the sidebar from loading until Redirect resolve
        // this loads faster and prevents the sidebar from setting isCurrent based on null match
        pathname !== '/' &&
          <>
            <SkipNavLink
              contentId={SKIP_NAVIGATION}
              style={srOnly}
            >
              Skip navigation
            </SkipNavLink>
            <SideNav />
          </>
      }
      <Suspense>
        {renderSpaceCheckRoute()}
      </Suspense>
      {
        spaceContext?.selectedSpaceId &&
          <PollGetSpaceAccessControlRequest
            onError={handleGetSpaceError}
            onSuccess={handlePollSuccess}
            spaceId={spaceContext.selectedSpaceId}
          />
      }
    </div>
  )
}

export default Router
